import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronDown, Route, Plane, Play, Pause } from "lucide-react";
import { Stars } from "@/components/Stars";
import { useContact } from "@/lib/contact";
import { useContent } from "@/context/ContentContext";

// يُختار ملف واحد فقط حسب الجهاز (جوال 720p / كمبيوتر 1080p) — بلا مسار صوتي
const pickHeroVideo = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
    ? "/media/hero-mobile.mp4"
    : "/media/hero-desktop.mp4";
const HERO_POSTER = "/media/hero-poster.webp";
const HERO_POSTER_SRCSET = "/media/hero-poster-720.webp 720w, /media/hero-poster.webp 1440w";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

export const Hero = () => {
  const CONTACT = useContact();
  const { content } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const [videoSrc] = useState(pickHeroVideo);
  const videoRef = useRef(null);
  // البوستر يظهر فورًا؛ الفيديو يُركَّب فقط بعد اكتمال التحميل الأولي (idle) حتى لا يبطئ الصفحة
  const [videoEnabled, setVideoEnabled] = useState(false);
  // loading | playing | paused | error — مربوطة بأحداث الفيديو الفعلية لا بنقرة الزر
  const [videoState, setVideoState] = useState("loading");

  useEffect(() => {
    const enable = () => setVideoEnabled(true);
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(enable, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(enable, 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onPlay = () => setVideoState("playing");
    const onPause = () => setVideoState("paused");
    const onError = () => setVideoState("error");
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("error", onError);
    // تشغيل تلقائي صامت داخل الصفحة؛ إن منعه المتصفح (وضع توفير الطاقة في iOS مثلًا) يظهر زر التشغيل
    Promise.resolve(v.play()).catch(() => setVideoState("paused"));
    if (!v.paused) setVideoState("playing");
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("error", onError);
    };
  }, [videoSrc, videoEnabled]);

  const toggleVideo = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (v.paused) {
        await v.play(); // نقرة المستخدم = إذن التشغيل — يعمل صامتًا وداخل الصفحة
      } else {
        v.pause();
      }
    } catch {
      setVideoState("error"); // فشل فعلي: نخفي الزر وتبقى الصورة البديلة واضحة
    }
  };

  const rating = content?.rating ?? 4.4;
  const reviewsCount = content?.reviewsCount ?? 223;

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_POSTER}
          srcSet={HERO_POSTER_SRCSET}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          fetchpriority="high"
          className="absolute inset-0 h-full w-full object-cover object-[38%_center] md:object-center"
        />
        {videoEnabled && (
          <video
            ref={videoRef}
            key={videoSrc}
            className="absolute inset-0 h-full w-full object-cover object-[38%_center] md:object-center"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_POSTER}
            aria-hidden="true"
            data-testid="hero-bg-video"
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/75 via-[#FFFFFF]/45 to-[#FFFFFF]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-l from-[#FFFFFF]/65 via-transparent to-[#C80000]/10" />
      </motion.div>

      {(videoState === "playing" || videoState === "paused") && (
        <button
          type="button"
          onClick={toggleVideo}
          data-testid="hero-video-toggle"
          aria-label={videoState === "playing" ? "إيقاف الفيديو" : "تشغيل الفيديو"}
          className="absolute z-20 bottom-6 start-5 h-11 w-11 rounded-full bg-black/35 backdrop-blur border border-white/40 text-white flex items-center justify-center hover:bg-black/50 transition-colors"
        >
          {videoState === "playing" ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 -me-0.5" />}
        </button>
      )}

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full pt-28 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs sm:text-sm font-semibold text-brand mb-8"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C80000] animate-pulse-brand" />
          وكالة سفر · منطقة السيف — مملكة البحرين
        </motion.p>

        <h1 data-testid="hero-title" className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.25] text-[#1A1A1E] max-w-3xl">
          <MaskedLine delay={0.3}>رحلتك القادمة</MaskedLine>
          <MaskedLine delay={0.45}>
            تبدأ من <span className="text-brand">قلب البحرين</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-6 max-w-xl text-sm sm:text-lg leading-relaxed text-slate-600"
        >
          دليل المسافر للحجوزات، وكالة سفر في منطقة السيف بمملكة البحرين. تواصلوا معنا للاستفسار عن رحلتكم وترتيب الحجوزات.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={CONTACT.phoneLink}
            data-testid="hero-cta-call"
            className="group inline-flex items-center gap-3 rounded-full bg-[#C80000] px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-[#E01010] hover:shadow-[0_10px_40px_-10px_rgba(200,0,0,0.7)]"
          >
            <Phone className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
            اتصل بنا
          </a>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="inline-flex items-center gap-3 rounded-full glass px-8 py-4 text-base font-bold text-[#1A1A1E] transition-all duration-300 hover:border-[#25D366]/50 hover:text-[#25D366]"
          >
            <WhatsAppIcon />
            تواصل معنا عبر واتساب
          </a>
          <Link
            to="/plan-trip"
            data-testid="hero-cta-plan"
            className="inline-flex items-center gap-3 rounded-full border-2 border-[#C80000] px-8 py-4 text-base font-bold text-brand transition-all duration-300 hover:bg-[#C80000] hover:text-white"
          >
            <Route className="h-5 w-5" />
            رتّب رحلتك
          </Link>
          <Link
            to="/flight-quote"
            data-testid="hero-cta-flight"
            className="inline-flex items-center gap-3 rounded-full border-2 border-slate-300 px-8 py-4 text-base font-bold text-slate-700 transition-all duration-300 hover:border-[#C80000] hover:text-brand"
          >
            <Plane className="h-5 w-5" />
            اطلب تسعير طيران
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.25 }}
        className="absolute bottom-24 end-5 sm:end-10 z-10 hidden sm:block"
        data-testid="hero-rating-card"
      >
        <div className="glass brand-ring rounded-2xl px-6 py-5 animate-float-slow">
          <div className="flex items-center gap-3">
            <span className="font-display text-4xl font-bold text-brand" dir="ltr">{rating}</span>
            <div className="flex flex-col gap-1">
              <Stars value={rating} size="h-3.5 w-3.5" />
              <span className="text-[11px] text-slate-600">
                على Google — <span dir="ltr">{reviewsCount}</span> تقييمًا
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-slate-600"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.div>
    </section>
  );
};
